import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Popover from "antd/es/popover";
import cn from "classnames";
import deepmerge from "deepmerge";

import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import {
  EntityType,
  EntityTypesSupportingRoutes,
  EntityTypesSupportingSavedFilters,
  EntityTypeSupportingGroups,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import {
  FilterOptionValue,
  IFilterOperatorConfig,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { ButtonProps } from "@mapmycustomers/ui";

import getFilteredFields from "@app/component/filtering/utils/getFilteredFields";
import useView from "@app/component/view/utils/useView";
import { getActivityTypes } from "@app/store/activity";
import { getFunnels } from "@app/store/deal";
import { getGroupsForEntity } from "@app/store/groups";
import { areCheckInFieldsVisible, getColors, getPosition } from "@app/store/iam";
import { getAllUsersOfEntireOrganization, getTeamsOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { getRoutesForEntity } from "@app/store/routes/selector";
import { getSelectedFilter } from "@app/store/savedFilters";
import FilterOperatorAction from "@app/types/FilterOperatorAction";
import useAnalytics from "@app/util/contexts/useAnalytics";
import removeUndefinedFields from "@app/util/removeUndefinedFields";
import { areFiltersEqual } from "@app/util/viewModel/comparators";

import cleanUpFilterModel from "../../utils/cleanUpFilterModel";
import getAppliedFiltersCount from "../../utils/getAppliedFiltersCount";
import hasIncompleteValue from "../../utils/hasIncompleteValue";
import useSavedFilterApplicator from "../../utils/useSavedFilterApplicator";
import FilterButton from "../FilterButton";

import FilterDropdown from "./components/FilterDropdown";
import Header from "./components/Header";
import styles from "./StandardFilterButton.module.scss";
import isUnsupportedFilter from "./utils/isUnsupportedFilter";

const takeAllOperators: FilterOperatorAction = (
  field: IField,
  defaultOperators: IFilterOperatorConfig[]
) => defaultOperators;

export interface StandardFilterButtonProps {
  buttonProps?: ButtonProps;
  buttonText?: string;
  className?: string;
  context: string;
  entityType: EntityType;
  filterOperators?: FilterOperatorAction;
  filterOptions?: Partial<Record<FilterOption, FilterOptionValue>>;
  // Fields which are not available for selection in field dropdown. Maybe we use quick filters and don't
  // want to duplicate them there and in filter dropdown. Or maybe some of them are implicit like
  // startAt field on Calendar View for activities and we don't give user an ability to edit these filters
  // directly. But even though we have a separate prop for such implicit fields (implicitFilterFields),
  // we wanna give more flexibility and encourage to specify such fields in hiddenFields prop again.
  hiddenFields?: IField[];
  hideSavedFilters?: boolean;
  // A list of implicit fields, see example above. For saved filters, we do not include any filter rules for
  // implicit fields into "total filters applied" number. We also preserve any existing filter rules for
  // implicit fields when applying a saved filter even if saved filter has a rule for such field too.
  implicitFilterFields?: IField[];
  offerFilters?: Partial<FilterModel>;
  onHidePopover?: () => void;
  popoverClassName?: string;
  // a function to process saved filter before applying it
  processSavedFilter?: (savedFilter: SavedFilter) => SavedFilter | undefined;
  selectedFilter?: SavedFilter;
  showColumnGroups?: boolean;
  zIndex?: number;
}

interface Props extends StandardFilterButtonProps {
  checkInFieldsVisibility: boolean;
}

const StandardFilterButton: React.FC<Props> = ({
  buttonProps,
  buttonText,
  checkInFieldsVisibility,
  className,
  context,
  entityType,
  filterOperators = takeAllOperators,
  filterOptions,
  hiddenFields,
  hideSavedFilters,
  implicitFilterFields,
  offerFilters,
  onHidePopover,
  popoverClassName,
  processSavedFilter,
  selectedFilter,
  showColumnGroups,
  zIndex,
}) => {
  const analyticIssuer = useAnalytics();

  const {
    fieldModel,
    updateFilterModel,
    updateViewAs,
    viewState: { filter, viewAs },
  } = useView();

  const applyFilterChanges = useCallback(
    (modifiedFilterModel: Partial<FilterModel>) => {
      const updatedFilterModel = cleanUpFilterModel(modifiedFilterModel);
      if (!areFiltersEqual(filter, removeUndefinedFields(updatedFilterModel) as FilterModel)) {
        updateFilterModel(updatedFilterModel);
      }
      // Ignoring because we know that fake viewAs field is always a SimpleCondition
      // @ts-ignore
      const updatedViewAs: undefined | User["id"] = modifiedFilterModel?.viewAs?.value;
      if (viewAs !== updatedViewAs) {
        updateViewAs(updatedViewAs);
      }
    },
    [filter, updateFilterModel, updateViewAs, viewAs]
  );

  const [modifiedFilterModel, setModifiedFilterModel, resetFilterModel] =
    useStateWithDebouncedListener(
      [
        useDynamicCallback((modifiedFilterModel: Partial<FilterModel>) => {
          if (!hasIncompleteValue(modifiedFilterModel)) {
            applyFilterChanges(modifiedFilterModel);
          }
        }),
        500,
      ],
      {} as Partial<FilterModel>
    );

  useEffect(() => {
    // calling resetFilterModel instead of setModifiedFilterModel will not cause state callback to fire
    // also, let's use a fake viewAs filter, which is needed for simpler viewAs integration into filters
    resetFilterModel({
      ...filter,
      viewAs: viewAs
        ? {
            operator: FilterOperator.EQUALS,
            value: viewAs,
          }
        : undefined,
    });
  }, [filter, resetFilterModel, viewAs]);

  const [popoverVisible, setPopoverVisibility] = useState<boolean>(false);

  const handleClickPopoverVisibilityChange = useCallback(
    (visible) => {
      setPopoverVisibility(visible);
      if (!visible) {
        // apply filters when popover is closed
        applyFilterChanges(modifiedFilterModel);
        onHidePopover?.();
      }
      analyticIssuer?.clicked(["Filters"]);
    },
    [analyticIssuer, applyFilterChanges, modifiedFilterModel, onHidePopover]
  );

  const filteredFields = useMemo(
    () => getFilteredFields(fieldModel, modifiedFilterModel, hiddenFields),
    [fieldModel, modifiedFilterModel, hiddenFields]
  );

  const filterCount = useMemo(
    () =>
      getAppliedFiltersCount(cleanUpFilterModel(modifiedFilterModel), viewAs, implicitFilterFields),
    [modifiedFilterModel, viewAs, implicitFilterFields]
  );

  useEffect(() => {
    if (offerFilters) {
      setModifiedFilterModel((currentModel) => deepmerge(currentModel, offerFilters));
      setPopoverVisibility(true);
    }
  }, [offerFilters, setModifiedFilterModel]);

  useSavedFilterApplicator(
    entityType as EntityTypesSupportingSavedFilters,
    selectedFilter,
    implicitFilterFields,
    processSavedFilter
  );

  const hasUnsupportedRules = isUnsupportedFilter(modifiedFilterModel, filteredFields);

  return (
    <Popover
      content={
        popoverVisible ? (
          <FilterDropdown
            checkInFieldsVisibility={checkInFieldsVisibility}
            context={context}
            entityType={entityType}
            fieldModel={fieldModel}
            filteredFields={filteredFields}
            filterModel={modifiedFilterModel}
            filterOperators={filterOperators}
            filterOptions={filterOptions}
            hiddenFields={hiddenFields}
            hideSavedFilters={hideSavedFilters}
            implicitFilterFields={implicitFilterFields}
            onChange={setModifiedFilterModel}
            selectedFilter={selectedFilter}
            showColumnGroups={showColumnGroups}
          />
        ) : null
      }
      onVisibleChange={handleClickPopoverVisibilityChange}
      overlayClassName={cn(styles.popoverContainer, popoverClassName)}
      placement="bottomLeft"
      title={
        <Header
          entityType={entityType as Exclude<EntityTypesSupportingSavedFilters, EntityType.PIN>}
          filterCount={filterCount}
          hideSavedFilters={hideSavedFilters}
          implicitFilterFields={implicitFilterFields}
          selectedFilter={selectedFilter}
        />
      }
      trigger="click"
      visible={popoverVisible}
      zIndex={zIndex}
    >
      <FilterButton
        buttonProps={buttonProps}
        buttonText={buttonText}
        className={className}
        disabled={hasUnsupportedRules}
        filterCount={filterCount}
        pressed={popoverVisible}
        selectedSavedFilter={selectedFilter}
      />
    </Popover>
  );
};

const mapStateToProps = (
  state: RootState,
  { entityType, filterOptions, hideSavedFilters }: StandardFilterButtonProps
) => ({
  checkInFieldsVisibility: areCheckInFieldsVisible(state),
  filterOptions: {
    [FilterOption.AVAILABLE_ACTIVITY_TYPES]: getActivityTypes(state) ?? [],
    [FilterOption.AVAILABLE_COLORS]: getColors(state) ?? [],
    [FilterOption.AVAILABLE_FUNNELS]: getFunnels(state) ?? [],
    [FilterOption.AVAILABLE_GROUPS]: getGroupsForEntity(state)(
      entityType as EntityTypeSupportingGroups
    ),
    [FilterOption.AVAILABLE_ROUTES]: getRoutesForEntity(state)(
      entityType as EntityTypesSupportingRoutes
    ),
    [FilterOption.AVAILABLE_TEAMS]: getTeamsOfEntireOrganization(state) ?? [],
    [FilterOption.AVAILABLE_USERS]: getAllUsersOfEntireOrganization(state) ?? [],
    [FilterOption.ENTITY_TYPE]: entityType,
    [FilterOption.USER_POSITION]: getPosition(state),
    ...filterOptions,
  },
  selectedFilter: hideSavedFilters
    ? undefined
    : getSelectedFilter(state)(
        entityType as Exclude<EntityTypesSupportingSavedFilters, EntityType.PIN>
      ),
});

export default connect(mapStateToProps)(StandardFilterButton);
